// SLASLX-3400: capture-phase click interceptor for the pravo-eu iframe.
//
// Routes anchor clicks through the parent portal-main so a user navigation
// produces exactly one history entry (1× browser Back). Without this, the
// iframe's react-router-dom Link pushed its own entry and the parent then
// pushed another → 2× Back required.
//
// Mirrors the staticky-obsah handler but with pravo-eu's basename and
// microservice prefix.

let listenerRegistered = false;

const PARENT_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_PORTAL_MAIN_URL || '').origin;
  } catch {
    return '*';
  }
})();

// BrowserRouter basename for this iframe; stripped from anchor.href so the
// path posted to parent is in parent-URL format.
const BASENAME = (process.env.REACT_APP_SUBPATH || '').replace(/\/+$/, '');

// This microservice's parent-URL prefix; prepended to iframe-internal paths
// (which don't carry the /pravo-eu/ prefix in the iframe's router).
const MS_PREFIX = '/pravo-eu';

// Other microservices: if a link resolves to one of these prefixes, the path
// is already in parent-URL format and must NOT get MS_PREFIX prepended.
const OTHER_MS_PREFIXES = [
  '/staticky-obsah-fe',
  '/tezaury',
  '/ezbierky',
  '/elegislativa',
  '/pripomienky',
  '/sudne-rozhodnutia',
  '/sprava-vyjadreni',
  '/vestniky',
];

const stripBasename = (pathname: string): string => {
  if (!BASENAME) return pathname;
  if (pathname === BASENAME) return '/';
  if (pathname.startsWith(`${BASENAME}/`)) return pathname.slice(BASENAME.length);
  return pathname;
};

// Only reached for same-origin links. This iframe is served under BASENAME, so
// a bare '/' can only be the portal shell itself, never a route of this
// microservice. Without a basename the two are indistinguishable, so leave the
// link alone rather than guess.
const isPortalHome = (pathname: string): boolean => !!BASENAME && pathname === '/';

const hasKnownPrefix = (pathname: string): boolean => {
  if (pathname === MS_PREFIX || pathname.startsWith(`${MS_PREFIX}/`)) return true;
  return OTHER_MS_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`));
};

const interceptClickEvent = (event: MouseEvent): void => {
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

  const { target } = event;
  if (!(target instanceof Element)) return;

  const anchor = target.closest('a');
  if (!(anchor instanceof HTMLAnchorElement)) return;

  const linkTarget = anchor.getAttribute('target');
  if (linkTarget && linkTarget.toLowerCase() !== '_self') return;

  if (anchor.hasAttribute('download')) return;

  const href = anchor.getAttribute('href');
  if (!href) return;
  if (href.startsWith('#')) return;

  let resolved: URL;
  try {
    resolved = new URL(anchor.href);
  } catch {
    return;
  }

  if (
    // eslint-disable-next-line no-script-url
    resolved.protocol === 'javascript:'
    || resolved.protocol === 'mailto:'
    || resolved.protocol === 'tel:'
  ) return;

  if (resolved.origin !== window.location.origin) {
    event.preventDefault();
    window.parent.postMessage({
      type: 'iframe-to-parent-absolute-url',
      route: anchor.href,
    }, PARENT_ORIGIN);
    return;
  }

  event.preventDefault();

  // The portal shell lives at the origin root and serves this iframe from the
  // same origin, so its home link resolves to pathname '/'. Prefixing that as
  // /pravo-eu/ pushed a history entry for a route that does not exist, so Back
  // after clicking the Domov breadcrumb landed on "Stránka nebola nájdená"
  // (SLASLX-4213 D). stopPropagation keeps the breadcrumb's own onClick from
  // posting a second entry for the same navigation.
  if (isPortalHome(resolved.pathname)) {
    event.stopPropagation();
    window.parent.postMessage({
      type: 'iframe-to-parent-external-route',
      route: '/',
    }, PARENT_ORIGIN);
    return;
  }

  const stripped = stripBasename(resolved.pathname);
  const parentPath = hasKnownPrefix(stripped) ? stripped : `${MS_PREFIX}${stripped}`;
  const route = parentPath + resolved.search + resolved.hash;
  const isExternal = OTHER_MS_PREFIXES.some(p => parentPath === p || parentPath.startsWith(`${p}/`));
  window.parent.postMessage({
    type: isExternal ? 'iframe-to-parent-external-route' : 'iframe-to-parent-internal-route',
    route,
  }, PARENT_ORIGIN);
};

const handleAbsoluteUrlRedirects = (): void => {
  if (listenerRegistered) return;
  if (!document.addEventListener) return;
  document.addEventListener('click', interceptClickEvent, true);
  listenerRegistered = true;
};

export default handleAbsoluteUrlRedirects;
